import { useRegisterSessionRestorer } from '@ais-ui/websockets';
import { useCallback } from 'react';
import { useMapOverlay } from './contexts/MapOverlayContext';
import { useFilterContext } from './FilterContext';

const OVERLAY_REFRESH_DELAY_MS = 100;

/**
 * Owns "apply the filter, then refresh the overlay" and registers it as the
 * `filter` session restorer: olserver keeps the filter on a per-user mapview
 * that can be reset while the web session survives, leaving the UI showing a
 * filter the server no longer applies (CLASSIC-10412).
 */
export function useFilterSessionRestorer(): (
  showToast: boolean,
) => Promise<boolean> {
  const { applyFilter } = useFilterContext();
  const { setTrigger } = useMapOverlay();

  const applyAndRefresh = useCallback(
    async (showToast: boolean) => {
      const success = await applyFilter(showToast);
      if (success) {
        setTimeout(() => {
          setTrigger((prevTrigger) => prevTrigger + 1);
        }, OVERLAY_REFRESH_DELAY_MS);
      }
      return success;
    },
    [applyFilter, setTrigger],
  );

  const restore = useCallback(async () => {
    if (!(await applyAndRefresh(false))) {
      throw new Error('setFilter failed');
    }
  }, [applyAndRefresh]);

  useRegisterSessionRestorer({ key: 'filter', restore });

  return applyAndRefresh;
}
